/**
 * The reward wallet on a customer's profile.
 *
 * Answers the three questions an agent on a call actually has: how many points does this
 * person have, when do they die, and what has moved. Nothing here can change the wallet —
 * manual credits are a separate, approved action, not a button beside a balance.
 *
 * When the programme is dormant the card says so plainly instead of showing a 0 that
 * looks like a customer with no points.
 */
import React, { useEffect, useState } from 'react';
import { Star, ChevronDown, AlertTriangle, RefreshCw } from 'lucide-react';
import { rewardsService } from '../services/rewardsService';
import { pts, inr, dateIn, daysFrom, dateTimeIn, LEDGER_LABEL, SOURCE_LABEL, isDebit } from './shared/rewardsFormat';

// Tailwind only keeps classes it can see in the source, so the tone is a lookup rather
// than an interpolated class name — `text-${tone}-800` would be purged out of the build.
const TONE = { slate: 'text-slate-800', violet: 'text-violet-700' };

const Stat = ({ label, value, tone = 'slate' }) => (
    <div className="text-center">
        <p className={`text-xl font-bold leading-tight ${TONE[tone] || TONE.slate}`}>{value}</p>
        <p className="text-[11px] text-slate-500 mt-0.5">{label}</p>
    </div>
);

const CustomerWalletCard = ({ mobile }) => {
    const [wallet, setWallet] = useState(null);
    const [ledger, setLedger] = useState(null);
    const [state, setState] = useState('loading');   // loading | ready | off | error
    const [err, setErr] = useState('');
    const [showHistory, setShowHistory] = useState(false);

    const load = async () => {
        if (!mobile) { setState('error'); setErr('No mobile number on this customer.'); return; }
        setState('loading'); setErr('');
        try {
            const [cfg, w] = await Promise.all([rewardsService.getConfig(), rewardsService.getWallet(mobile)]);
            if (cfg.config?.status !== 'active') { setWallet(w.wallet); setState('off'); return; }
            setWallet(w.wallet);
            setState('ready');
        } catch (e) { setErr(e.message); setState('error'); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [mobile]);

    const openHistory = async () => {
        const next = !showHistory;
        setShowHistory(next);
        if (next && !ledger) {
            try { setLedger((await rewardsService.getLedger(mobile, 100)).rows || []); }
            catch (e) { setLedger([]); setErr(e.message); }
        }
    };

    const w = wallet;
    const expiringSoon = w?.expiringIn30 > 0;

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <Star size={16} className="text-violet-600" />
                <h3 className="font-bold text-slate-800 text-sm">Reward Wallet</h3>
                {state === 'off' && <span className="ml-auto text-[10px] font-bold uppercase tracking-wide bg-slate-100 text-slate-500 px-2 py-0.5 rounded">Programme off</span>}
                {state !== 'off' && (
                    <button type="button" onClick={load} className="ml-auto text-slate-400 hover:text-slate-700" title="Refresh">
                        <RefreshCw size={13} className={state === 'loading' ? 'animate-spin' : ''} />
                    </button>
                )}
            </div>

            {state === 'loading' && <div className="px-4 py-6 text-center text-sm text-slate-400">Reading the wallet…</div>}

            {state === 'error' && (
                <div className="px-4 py-4 text-sm text-red-600 flex items-start gap-2">
                    <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                    <div>{err || 'Could not read the wallet.'}</div>
                </div>
            )}

            {(state === 'ready' || state === 'off') && w && (
                <>
                    {state === 'off' && (
                        <p className="px-4 pt-3 text-[11.5px] text-slate-500">
                            The rewards programme has not been launched, so no points are being earned or redeemed yet.
                            {w.outstanding > 0 ? ' The balance below was created before launch.' : ''}
                        </p>
                    )}

                    <div className="px-4 py-4 grid grid-cols-3 gap-2">
                        <Stat label="Available" value={pts(w.available)} tone="violet" />
                        <Stat label="Held for an order" value={pts(w.reserved)} />
                        <Stat label="Worth" value={inr(w.outstanding)} />
                    </div>

                    {expiringSoon && (
                        <div className="mx-4 mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            <p className="text-[12px] font-bold text-amber-800">{pts(w.expiringIn30)} points expire within 30 days</p>
                            <p className="text-[11px] text-amber-700">
                                Earliest {dateIn(w.earliestExpiry)} ({daysFrom(w.earliestExpiry)}).
                            </p>
                        </div>
                    )}

                    <div className="px-4 pb-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[11.5px]">
                        <div className="flex justify-between"><span className="text-slate-500">Earned, all time</span><span className="font-semibold">{pts(w.lifetime?.earned)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Promotional</span><span className="font-semibold">{pts(w.lifetime?.promotional)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Redeemed</span><span className="font-semibold">{pts(w.lifetime?.redeemed)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Expired</span><span className="font-semibold">{pts(w.lifetime?.expired)}</span></div>
                        {w.recovery > 0 && (
                            <div className="col-span-2 flex justify-between text-rose-700">
                                <span>Recovery owed (spent before a reversal)</span><span className="font-bold">{pts(w.recovery)}</span>
                            </div>
                        )}
                    </div>

                    {/* The lots, because "when do my points expire" has more than one answer. */}
                    {w.lots?.some(l => l.remaining > 0) && (
                        <div className="px-4 pb-3">
                            <p className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">Live grants</p>
                            <div className="space-y-1">
                                {w.lots.filter(l => l.remaining > 0 && (!l.expiresAt || l.expiresAt > Date.now()))
                                    .sort((a, b) => (a.expiresAt || Infinity) - (b.expiresAt || Infinity))
                                    .map(l => (
                                        <div key={l.id} className="flex items-center gap-2 text-[11.5px] bg-slate-50 rounded-lg px-2.5 py-1.5">
                                            <span className="font-bold text-slate-700 w-10">{pts(l.remaining)}</span>
                                            <span className="text-slate-500 flex-1 truncate">
                                                {SOURCE_LABEL[l.source] || l.source}
                                                {l.orderId ? ` · ${l.orderId}` : ''}
                                            </span>
                                            <span className={l.expiresAt && l.expiresAt - Date.now() < 30 * 86400000 ? 'text-amber-700 font-semibold' : 'text-slate-400'}>
                                                {l.expiresAt ? dateIn(l.expiresAt) : 'no expiry'}
                                            </span>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    <button type="button" onClick={openHistory} className="w-full px-4 py-2.5 border-t border-slate-100 flex items-center justify-between text-[12px] font-semibold text-slate-600 hover:bg-slate-50">
                        <span>Movement history</span>
                        <ChevronDown size={15} className={`transition-transform ${showHistory ? 'rotate-180' : ''}`} />
                    </button>

                    {showHistory && (
                        <div className="max-h-72 overflow-y-auto divide-y divide-slate-50">
                            {!ledger && <div className="px-4 py-4 text-center text-xs text-slate-400">Loading…</div>}
                            {ledger?.length === 0 && <div className="px-4 py-4 text-center text-xs text-slate-400">Nothing has moved in this wallet yet.</div>}
                            {ledger?.map(r => (
                                <div key={r.id} className="px-4 py-2 flex items-start gap-2 text-[11.5px]">
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-slate-700">{LEDGER_LABEL[r.type] || r.type}</p>
                                        <p className="text-slate-400 truncate">
                                            {r.narration || r.reason || r.orderRef || r.orderId || ''}
                                            {r.actor && r.actor !== 'system' ? ` · ${r.actor}` : ''}
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        {typeof r.points === 'number' && r.points > 0 && (
                                            <p className={`font-bold ${isDebit(r.type) ? 'text-rose-600' : 'text-emerald-600'}`}>
                                                {isDebit(r.type) ? '−' : '+'}{pts(r.points)}
                                            </p>
                                        )}
                                        <p className="text-slate-400">{dateTimeIn(r.at)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CustomerWalletCard;
